/**
 * Admin — feedback and demand.
 *
 * Three views, in the order they are useful:
 *
 *  1. **Demand** — what teachers cannot explain today and what they want built,
 *     sorted so the loudest gaps surface. This is the roadmap, as reported.
 *  2. **Responses** — everything, filterable, with the free text in full.
 *  3. **Summary** — per-question DISTRIBUTIONS, not averages. "3.9 average"
 *     hides a bimodal split between teachers who would use it tomorrow and
 *     teachers who never would, which is the single most important thing this
 *     page can show.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { Loader2, Download, RefreshCw, Filter } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  adminListFeedback, adminFeedbackSummary, adminUpdateFeedback, feedbackExportUrl,
  type FeedbackRow, type FeedbackSummary, type FeedbackFilters,
} from '../../services/feedbackAPI';
import { describeAnswer, formByKey } from '../../components/learn/feedbackForms';

export const Route = createFileRoute('/admin/feedback')({ component: AdminFeedback });

const STATUSES = ['NEW', 'REVIEWED', 'PLANNED', 'SHIPPED', 'DECLINED'] as const;
const TABS = ['demand', 'responses', 'summary'] as const;
type Tab = (typeof TABS)[number];

const card = 'rounded-2xl border border-black/10 bg-white dark:border-white/10 dark:bg-white/[0.04]';
const muted = 'text-[#6C6879] dark:text-[#A5A2B5]';

function AdminFeedback() {
  const [tab, setTab] = useState<Tab>('demand');
  const [filters, setFilters] = useState<FeedbackFilters>({});
  const [rows, setRows] = useState<FeedbackRow[]>([]);
  const [summary, setSummary] = useState<FeedbackSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const f: FeedbackFilters = { ...filters, limit: 500 };
      if (tab === 'demand') f.withText = true;
      const [list, sum] = await Promise.all([
        adminListFeedback(f),
        tab === 'summary' ? adminFeedbackSummary(filters) : Promise.resolve(null),
      ]);
      setRows(list.items);
      if (sum) setSummary(sum);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, [filters, tab]);

  useEffect(() => { void load(); }, [load]);

  // Demand = anything carrying a gap or a request, loudest first. Priority is
  // the teacher's own weighting, so it leads the sort.
  const demand = rows
    .filter((r) => r.cannotExplain || r.wishlist)
    .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0) || (b.submittedAt > a.submittedAt ? 1 : -1));

  return (
    <div className="mx-auto max-w-[1180px] px-6 py-10">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[28px] font-bold tracking-tight">Feedback &amp; demand</h1>
          <p className={cn('mt-1 text-sm', muted)}>
            What teachers and students said — and what they asked us to build.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => void load()} className={cn(card, 'inline-flex items-center gap-1.5 px-3 py-2 text-[13px] font-semibold')}>
            <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} /> Refresh
          </button>
          <a href={feedbackExportUrl(filters)} className={cn(card, 'inline-flex items-center gap-1.5 px-3 py-2 text-[13px] font-semibold')}>
            <Download className="h-3.5 w-3.5" /> CSV
          </a>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              'rounded-full px-4 py-1.5 text-[13px] font-semibold capitalize transition-colors',
              tab === t ? 'bg-[#5B2EE5] text-white' : cn(card, muted),
            )}
          >
            {t}
          </button>
        ))}
      </div>

      <div className={cn(card, 'mt-4 flex flex-wrap items-center gap-2 p-3')}>
        <Filter className={cn('h-3.5 w-3.5', muted)} />
        <Select value={filters.role ?? ''} onChange={(v) => setFilters((f) => ({ ...f, role: v || undefined }))}
          options={[['', 'Any role'], ['TEACHER', 'Teachers'], ['STUDENT', 'Students'], ['PARENT', 'Parents']]} />
        <Select value={filters.subject ?? ''} onChange={(v) => setFilters((f) => ({ ...f, subject: v || undefined }))}
          options={[['', 'Any subject'], ['VIDEO', 'Video'], ['ASSESSMENT', 'Assessment'], ['TOPIC', 'Topic'], ['FRAMEWORK', 'Framework']]} />
        <Select value={filters.status ?? ''} onChange={(v) => setFilters((f) => ({ ...f, status: v || undefined }))}
          options={[['', 'Any status'], ...STATUSES.map((s) => [s, s.toLowerCase()] as [string, string])]} />
        <input
          value={filters.source ?? ''}
          onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value || undefined }))}
          placeholder="source (e.g. demo-mumbai)"
          className="rounded-lg border border-black/10 bg-transparent px-2.5 py-1.5 text-[13px] outline-none dark:border-white/10"
        />
        <span className={cn('ml-auto text-[12.5px] tabular-nums', muted)}>{rows.length} shown</span>
      </div>

      {error && <p className="mt-4 text-sm text-[#B4341F] dark:text-[#FF8A73]">{error}</p>}
      {loading && <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin" /></div>}

      {!loading && tab === 'demand' && (
        <div className="mt-4 flex flex-col gap-3">
          {demand.length === 0 && <Empty text="No gaps or requests captured yet." />}
          {demand.map((r) => <DemandCard key={r.id} row={r} onChanged={load} />)}
        </div>
      )}

      {!loading && tab === 'responses' && (
        <div className="mt-4 flex flex-col gap-3">
          {rows.length === 0 && <Empty text="No responses yet." />}
          {rows.map((r) => <ResponseCard key={r.id} row={r} />)}
        </div>
      )}

      {!loading && tab === 'summary' && summary && <SummaryView summary={summary} />}
    </div>
  );
}

function Select({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: [string, string][] }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-lg border border-black/10 bg-transparent px-2.5 py-1.5 text-[13px] capitalize outline-none dark:border-white/10 dark:bg-[#1a1a24]"
    >
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

function Empty({ text }: { text: string }) {
  return <div className={cn(card, 'px-6 py-12 text-center text-sm', muted)}>{text}</div>;
}

/** Which video the response is about — the first thing a reader needs. */
function About({ row }: { row: FeedbackRow }) {
  const where = [row.lesson?.className, row.lesson?.chapter ?? row.chapter].filter(Boolean).join(' · ');
  const title = row.lesson?.title ?? (row.shareToken ? 'A lesson no longer in the catalogue' : null);
  if (!title && !where) return null;
  return (
    <p className="mb-2 text-[13.5px]">
      {title && <span className="font-semibold">{title}</span>}
      {where && <span className={muted}>{title ? ' · ' : ''}{where}</span>}
      {row.shareToken && (
        <a
          href={`/analytics/${row.shareToken}`}
          className={cn('ml-2 text-[12px] underline underline-offset-2 hover:text-[#5B2EE5]', muted)}
        >
          open analytics
        </a>
      )}
    </p>
  );
}

function Who({ row }: { row: FeedbackRow }) {
  const bits = [row.name, row.organisation].filter(Boolean).join(' · ');
  return (
    <span className={cn('text-[12px]', muted)}>
      {row.role.toLowerCase()}
      {bits ? ` · ${bits}` : ' · anonymous'}
      {row.source ? ` · ${row.source}` : ''}
      {` · ${new Date(row.submittedAt).toLocaleDateString()}`}
    </span>
  );
}

function DemandCard({ row, onChanged }: { row: FeedbackRow; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const setStatus = async (s: string) => {
    setBusy(true);
    try { await adminUpdateFeedback(row.id, s); onChanged(); } finally { setBusy(false); }
  };
  return (
    <div className={cn(card, 'p-5')}>
      <About row={row} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Who row={row} />
        <div className="flex items-center gap-1.5">
          {row.priority != null && (
            <span className="rounded-full bg-[#5B2EE5]/12 px-2 py-0.5 text-[11.5px] font-bold text-[#5B2EE5] dark:text-[#BFA8FA]">
              priority {row.priority}/5
            </span>
          )}
          <select
            value={row.status}
            disabled={busy}
            onChange={(e) => void setStatus(e.target.value)}
            className="rounded-lg border border-black/10 bg-transparent px-2 py-1 text-[12px] font-semibold capitalize outline-none dark:border-white/10 dark:bg-[#1a1a24]"
          >
            {STATUSES.map((s) => <option key={s} value={s}>{s.toLowerCase()}</option>)}
          </select>
        </div>
      </div>

      {row.cannotExplain && (
        <div className="mt-3">
          <p className={cn('text-[11px] font-bold uppercase tracking-[0.1em]', muted)}>Cannot explain today</p>
          <p className="mt-1 whitespace-pre-wrap text-[14.5px] leading-relaxed">{row.cannotExplain}</p>
        </div>
      )}
      {row.wishlist && (
        <div className="mt-3">
          <p className={cn('text-[11px] font-bold uppercase tracking-[0.1em]', muted)}>Wants us to build</p>
          <p className="mt-1 whitespace-pre-wrap text-[14.5px] leading-relaxed">{row.wishlist}</p>
        </div>
      )}
      {row.comment && <p className={cn('mt-3 whitespace-pre-wrap text-[13.5px] leading-relaxed', muted)}>{row.comment}</p>}
      {row.email && <p className={cn('mt-1 text-[12px]', muted)}>{row.email}</p>}
    </div>
  );
}

function ResponseCard({ row }: { row: FeedbackRow }) {
  const form = formByKey(row.formKey);
  return (
    <div className={cn(card, 'p-5')}>
      <About row={row} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-[13px] font-bold">{form?.title ?? row.formKey}</span>
        <Who row={row} />
      </div>
      <div className="mt-3 flex flex-col gap-1.5">
        {Object.entries(row.answers ?? {}).map(([qid, v]) => {
          const { prompt, display } = describeAnswer(row.formKey, qid, v);
          return (
            <div key={qid} className="flex flex-wrap gap-x-2 text-[13.5px]">
              <span className={muted}>{prompt}</span>
              <span className="font-semibold">{display}</span>
            </div>
          );
        })}
      </div>
      {row.comment && <p className="mt-3 whitespace-pre-wrap text-[14px] leading-relaxed">{row.comment}</p>}
      {row.cannotExplain && <p className="mt-2 whitespace-pre-wrap text-[14px] leading-relaxed">{row.cannotExplain}</p>}
      {row.wishlist && <p className="mt-2 whitespace-pre-wrap text-[14px] leading-relaxed">{row.wishlist}</p>}
    </div>
  );
}

function SummaryView({ summary }: { summary: FeedbackSummary }) {
  return (
    <div className="mt-4 flex flex-col gap-4">
      <div className={cn(card, 'p-5')}>
        <p className={cn('text-[11px] font-bold uppercase tracking-[0.1em]', muted)}>Who answered</p>
        <div className="mt-2 flex flex-wrap gap-4">
          {Object.entries(summary.byRole).map(([role, n]) => (
            <span key={role} className="text-[15px]">
              <b className="tabular-nums">{n}</b> <span className={muted}>{role.toLowerCase()}</span>
            </span>
          ))}
          {Object.keys(summary.byRole).length === 0 && <span className={cn('text-sm', muted)}>Nothing yet.</span>}
        </div>
      </div>

      {Object.entries(summary.byForm).map(([formKey, data]) => {
        const form = formByKey(formKey);
        return (
          <div key={formKey} className={cn(card, 'p-5')}>
            <div className="flex items-baseline justify-between">
              <h2 className="text-[16px] font-bold">{form?.title ?? formKey}</h2>
              <span className={cn('text-[12.5px] tabular-nums', muted)}>{data.count} responses</span>
            </div>
            <div className="mt-4 flex flex-col gap-4">
              {Object.entries(data.questions).map(([qid, counts]) => {
                const total = Object.values(counts).reduce((a, b) => a + b, 0) || 1;
                const q = form?.questions.find((x) => x.id === qid);
                return (
                  <div key={qid}>
                    <p className="text-[13.5px] font-semibold">{q?.prompt ?? qid}</p>
                    <div className="mt-1.5 flex flex-col gap-1">
                      {Object.entries(counts)
                        .sort((a, b) => b[1] - a[1])
                        .map(([value, n]) => {
                          const label = q?.choices?.find((c) => c.value === value)?.label ?? value;
                          return (
                            <div key={value} className="flex items-center gap-2">
                              <span className={cn('w-[150px] shrink-0 truncate text-[12.5px]', muted)}>{label}</span>
                              <span className="h-2 flex-1 overflow-hidden rounded-full bg-black/[0.06] dark:bg-white/10">
                                <span className="block h-full rounded-full bg-[#5B2EE5]" style={{ width: `${(n / total) * 100}%` }} />
                              </span>
                              <span className="w-8 shrink-0 text-right text-[12px] font-semibold tabular-nums">{n}</span>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
